import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { colors } from '../../utils/colors';
import type { Teacher, ActiveTab } from '../../utils/types';
import type {
  SubjectAssignment, ExamStudent, ExamSubjectMeta, ExamComponent,
  ExamSubjectComponent,
} from './types';
import BottomNav from '../../components/BottomNav';
import {
  getOngoingExamList, getExamSubjectsList, getExamStudentList, addExamMarks,
  type OngoingExamListItem, type ExamSubjectListItem, type ExamType,
  type ExamStudentListResponse,
  type AddMarksStudentEntry,
} from '../../Api/exams';
import SlideToSubmit from '../../components/SlideToSubmit';

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  teacher: Teacher;
  onBack: () => void;
  onNavigate: (tab: ActiveTab) => void;
}

type View = 'exams' | 'assignments' | 'students';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function fmtShortDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
}

function fmtRange(startISO: string, endISO: string): string {
  if (startISO === endISO) return fmtShortDate(startISO);
  return `${fmtShortDate(startISO)} - ${fmtShortDate(endISO)}`;
}

const EXAM_TYPE_LABEL: Record<ExamType, string> = {
  mandatory: 'Mandatory',
  optional:  'Optional',
};

/** Convert one API subject row into a `SubjectAssignment` (used for the
 *  View 2 card + roster identity). The subject-level API row only carries
 *  aggregate max/min - the component breakdown comes from the students
 *  endpoint, so the `meta` returned here uses a single 'total' component
 *  purely for the compact View 2 chips. */
function toAssignmentAndMeta(item: ExamSubjectListItem): {
  assignment: SubjectAssignment;
  meta: ExamSubjectMeta;
} {
  const assignment: SubjectAssignment = {
    assignmentId: `c${item.class_id}s${item.section_id}sub${item.subject_id}`,
    classId:      item.class_id,
    className:    item.class_name,
    sectionId:    item.section_id,
    sectionName:  item.section_name,
    subjectId:    item.subject_id,
    subjectName:  item.subject_name,
    studentCount: item.student_count,
  };
  const meta: ExamSubjectMeta = {
    subjectId:   item.subject_id,
    subjectName: item.subject_name,
    minMarks:    item.min_marks,
    maxMarks:    item.max_marks,
    components: [{ type: 'total', label: 'Marks', maxMarks: item.max_marks }],
  };
  return { assignment, meta };
}

/** Build `ExamSubjectMeta` (with proper theory/practical/oral components)
 *  from the students-endpoint response, honouring the `has_*` flags. */
function metaFromStudentListResponse(resp: ExamStudentListResponse): ExamSubjectMeta {
  const components: ExamSubjectComponent[] = [];
  if (resp.has_theory && resp.theory_max_marks > 0) {
    components.push({ type: 'theory', label: 'Theory', maxMarks: resp.theory_max_marks });
  }
  if (resp.has_practical && resp.practical_max_marks > 0) {
    components.push({ type: 'practical', label: 'Practical', maxMarks: resp.practical_max_marks });
  }
  if (resp.has_oral && resp.oral_max_marks > 0) {
    components.push({ type: 'oral', label: 'Oral', maxMarks: resp.oral_max_marks });
  }
  // If none of the component flags are set, treat the subject as a single
  // total-marks input so the UI still has something to render.
  if (components.length === 0) {
    components.push({ type: 'total', label: 'Marks', maxMarks: resp.max_marks });
  }
  return {
    subjectId:   resp.subject_id,
    subjectName: resp.subject_name,
    minMarks:    resp.min_marks,
    maxMarks:    resp.max_marks,
    components,
  };
}

/** Map API student rows into the internal `ExamStudent` shape, folding
 *  each row's theory/practical/oral values (when present) into
 *  `marks[subjectId]` per component. */
function rosterFromStudentListResponse(resp: ExamStudentListResponse): ExamStudent[] {
  return resp.students.map((s) => {
    const perComponent: Partial<Record<ExamComponent, number | null>> = {};
    if (resp.has_theory)    perComponent.theory    = s.theory;
    if (resp.has_practical) perComponent.practical = s.practical;
    if (resp.has_oral)      perComponent.oral      = s.oral ?? null;
    return {
      id:      s.student_id,
      name:    s.student_name,
      rollNo:  s.roll_number,
      uid:     s.student_uid,
      marks:   { [resp.subject_id]: perComponent },
    };
  });
}

// ─── Page shell ───────────────────────────────────────────────────────────────

const PageShell: React.FC<{
  title: string;
  onBack: () => void;
  children: React.ReactNode;
}> = ({ title, onBack, children }) => (
  <div style={{
    height: '100dvh', background: '#FDFCFE',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
    fontFamily: "'Inter', sans-serif", position: 'relative',
  }}>
    <div style={{
      padding: '30px 20px 14px',
      display: 'flex', alignItems: 'center', gap: 12,
      borderBottom: `1px solid ${colors.borderLight}`,
      background: '#fff', flexShrink: 0, zIndex: 10,
    }}>
      <button onClick={onBack} style={{
        background: 'none', border: 'none', cursor: 'pointer', padding: 0,
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 16, fontWeight: 500, color: colors.text,
      }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
          stroke={colors.text} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12" />
          <polyline points="12 19 5 12 12 5" />
        </svg>
        {title}
      </button>
    </div>
    {children}
  </div>
);

// ─── Sub-components ───────────────────────────────────────────────────────────

const ExamCard: React.FC<{
  exam: OngoingExamListItem;
  index: number;
  onSelect: (e: OngoingExamListItem) => void;
}> = ({ exam, index, onSelect }) => (
  <button
    onClick={() => onSelect(exam)}
    style={{
      width: '100%', padding: '16px 18px',
      background: '#fff',
      border: `1.5px solid ${colors.borderLight}`,
      borderRadius: 14, cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 14,
      textAlign: 'left', fontFamily: 'inherit',
      animation: `slideUp 0.25s ease ${index * 0.04}s both`,
    }}
  >
    <div
      style={{
        width: 44, height: 44, borderRadius: 12,
        background: '#dcfce7',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke="#166534" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="9" y1="15" x2="15" y2="15" />
        <line x1="9" y1="11" x2="15" y2="11" />
      </svg>
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 15, fontWeight: 800, color: colors.text }}>
        {exam.name}
      </div>
      <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 3, fontWeight: 600 }}>
        {EXAM_TYPE_LABEL[exam.exam_type] ?? exam.exam_type} · {fmtRange(exam.start_date, exam.end_date)}
      </div>
      <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
        <span
          style={{
            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
            background: colors.primaryBg, color: colors.primary,
            border: `1px solid ${colors.borderLight}`,
          }}
        >
          {exam.subject_count} subject{exam.subject_count !== 1 ? 's' : ''}
        </span>
      </div>
    </div>
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke={colors.textMuted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  </button>
);

const AssignmentCard: React.FC<{
  assignment: SubjectAssignment;
  examMeta: ExamSubjectMeta | null;
  index: number;
  onSelect: (a: SubjectAssignment) => void;
}> = ({ assignment, examMeta, index, onSelect }) => (
  <button
    onClick={() => onSelect(assignment)}
    style={{
      width: '100%', padding: '16px 18px',
      background: colors.primaryBg,
      border: `1.5px solid ${colors.borderLight}`,
      borderRadius: 14, cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 14,
      textAlign: 'left', fontFamily: 'inherit',
      animation: `slideUp 0.25s ease ${index * 0.04}s both`,
    }}
  >
    <div style={{
      width: 44, height: 44, borderRadius: 12, background: '#EEE3F7',
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke={colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
      </svg>
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: colors.primary, marginBottom: 3 }}>
        {assignment.className} - Sec {assignment.sectionName}
      </div>
      <div style={{ fontSize: 12, color: colors.textMuted, fontWeight: 600 }}>
        {assignment.subjectName} · {assignment.studentCount} students
      </div>
      {examMeta && (
        <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
          <span
            style={{
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
              background: '#fff', color: colors.primary,
              border: `1px solid ${colors.borderLight}`,
            }}
          >
            Max {examMeta.maxMarks}
          </span>
          <span
            style={{
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
              background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0',
            }}
          >
            Min {examMeta.minMarks}
          </span>
        </div>
      )}
    </div>
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke={colors.textMuted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  </button>
);

// ─── Helpers used by the row ──────────────────────────────────────────────

/**
 * Short single-letter labels used in the compact inline row layout:
 *   W = Written (theory), P = Practical, O = Oral.
 */
function shortComponentLabel(type: ExamComponent): string {
  switch (type) {
    case 'theory':    return 'W';
    case 'practical': return 'P';
    case 'oral':      return 'O';
    case 'total':     return '';
  }
}

/** Sum of all saved (non-null) component marks for a subject. */
function savedTotal(
  savedComponents: Partial<Record<ExamComponent, number | null>> | undefined,
  components: readonly { type: ExamComponent }[],
): number {
  if (!savedComponents) return 0;
  let sum = 0;
  for (const c of components) {
    const v = savedComponents[c.type];
    if (typeof v === 'number') sum += v;
  }
  return sum;
}

/** All components have a non-null saved value. */
function isFullySaved(
  savedComponents: Partial<Record<ExamComponent, number | null>> | undefined,
  components: readonly { type: ExamComponent }[],
): boolean {
  if (!savedComponents) return false;
  return components.every((c) => {
    const v = savedComponents[c.type];
    return typeof v === 'number';
  });
}

/**
 * A student's row. Rendered in one of two modes controlled from above:
 * - view: shows the saved marks total (or a "Not entered" pill)
 * - edit: shows one number input per subject component (theory /
 *   practical / oral). All rows enter edit mode together via the
 *   global "Add marks" toggle.
 *
 * Validation:
 * - Each component input is capped at its own `maxMarks`.
 * - The overall pass/fail is judged from the *sum* of all components
 *   against `subject.minMarks`.
 */
const StudentMarksRow: React.FC<{
  student: ExamStudent;
  subject: ExamSubjectMeta;
  index: number;
  isEditMode: boolean;
  drafts: Partial<Record<ExamComponent, string>>;
  onDraftChange: (studentId: number, component: ExamComponent, next: string) => void;
}> = ({ student, subject, index, isEditMode, drafts, onDraftChange }) => {
  const savedComponents = student.marks[subject.subjectId];
  const hasSaved = isFullySaved(savedComponents, subject.components);
  const savedSum = hasSaved ? savedTotal(savedComponents, subject.components) : 0;
  const savedPct = hasSaved ? Math.round((savedSum / subject.maxMarks) * 100) : null;
  const savedPassed = hasSaved && savedSum >= subject.minMarks;

  const isSingle = subject.components.length === 1;

  // Edit-mode: per-component validation. A row is invalid if any component
  // draft is > its own max, or < 0, or non-numeric.
  const componentValidation = subject.components.map((c) => {
    const raw = (drafts[c.type] ?? '').trim();
    if (raw === '') return { filled: false, value: null as number | null, invalid: false };
    const n = Number(raw);
    const invalid = isNaN(n) || n < 0 || n > c.maxMarks;
    return { filled: true, value: invalid ? null : n, invalid };
  });
  const anyInvalid = componentValidation.some((v) => v.invalid);

  const rollBg = isEditMode
    ? colors.primaryDark
    : !hasSaved
      ? colors.primary
      : savedPassed ? '#22c55e' : '#e04444';
  const rowBg = isEditMode
    ? '#FBF8FD'
    : !hasSaved
      ? '#fff'
      : savedPassed ? '#f0fdf4' : '#fee2e2';
  const rowBorder = isEditMode
    ? colors.primary
    : !hasSaved
      ? colors.borderLight
      : savedPassed ? '#86efac' : '#fecaca';

  return (
    <div
      style={{
        width: '100%', background: rowBg,
        border: `1.5px solid ${isEditMode && anyInvalid ? colors.danger : rowBorder}`,
        borderRadius: 14, padding: '10px 14px',
        display: 'flex', flexDirection: 'column', gap: 8,
        animation: `slideUp 0.2s ease ${Math.min(index * 0.02, 0.2)}s both`,
        transition: 'background 0.2s, border-color 0.2s',
      }}
    >
      {/* ── Top row: roll + student name + right-side content ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          style={{
            width: 40, height: 40, borderRadius: '50%',
            background: rollBg, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, fontSize: 13, fontWeight: 800, color: '#fff',
          }}
        >
          {student.rollNo}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14, fontWeight: 700, color: colors.text,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}
          >
            {student.name}
          </div>
          <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2 }}>
            {student.uid}
          </div>
        </div>

        {isEditMode && isSingle ? (
          // ── EDIT MODE: single-component subject (input on same row) ──
          (() => {
            const c = subject.components[0];
            const v = componentValidation[0];
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={drafts[c.type] ?? ''}
                  onChange={(e) => onDraftChange(student.id, c.type, e.target.value)}
                  placeholder="-"
                  style={{
                    width: 76, padding: '8px 10px', boxSizing: 'border-box',
                    border: `1.5px solid ${v.invalid ? colors.danger : v.filled ? colors.primary : colors.border}`,
                    borderRadius: 10, fontSize: 15, fontWeight: 800,
                    fontFamily: 'inherit', outline: 'none',
                    color: colors.text, background: v.invalid ? colors.dangerBg : '#fff',
                    textAlign: 'center',
                  }}
                />
                <span style={{ fontSize: 11, color: colors.textMuted, fontWeight: 600 }}>
                  /{c.maxMarks}
                </span>
              </div>
            );
          })()
        ) : !isEditMode && hasSaved ? (
          // ── VIEW MODE: saved marks (total on right of top row) ──
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: savedPassed ? '#166534' : '#991b1b', lineHeight: 1 }}>
              {savedSum}
              <span style={{ fontSize: 11, color: colors.textMuted, fontWeight: 600 }}>
                /{subject.maxMarks}
              </span>
            </div>
            <div style={{ fontSize: 10, fontWeight: 700, color: savedPassed ? '#166534' : '#991b1b', marginTop: 3 }}>
              {savedPct}% · {savedPassed ? 'PASS' : 'FAIL'}
            </div>
          </div>
        ) : !isEditMode && !hasSaved ? (
          // ── VIEW MODE: not fully entered yet ──
          <span
            style={{
              fontSize: 11, fontWeight: 700, padding: '5px 12px', borderRadius: 20,
              background: '#fffbeb', color: '#d97706', border: '1px solid #fcd34d',
              whiteSpace: 'nowrap',
            }}
          >
            Not entered
          </span>
        ) : null}
      </div>

      {/* ── Multi-component inputs (edit mode, >1 components) ──
          One horizontal row: W / P / O inline, each with its own /max. */}
      {isEditMode && !isSingle && (
        <div
          style={{
            paddingLeft: 52, display: 'flex', gap: 8,
            justifyContent: 'flex-start', flexWrap: 'nowrap',
          }}
        >
          {subject.components.map((c, idx) => {
            const v = componentValidation[idx];
            return (
              <div
                key={c.type}
                style={{
                  flex: 1, minWidth: 0,
                  display: 'flex', alignItems: 'center', gap: 4,
                }}
              >
                <span
                  style={{
                    fontSize: 12, fontWeight: 800, color: colors.textMuted,
                    width: 14, textAlign: 'center', flexShrink: 0,
                  }}
                  title={c.label}
                >
                  {shortComponentLabel(c.type)}
                </span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  value={drafts[c.type] ?? ''}
                  onChange={(e) => onDraftChange(student.id, c.type, e.target.value)}
                  placeholder="-"
                  style={{
                    flex: 1, minWidth: 0, padding: '6px 4px', boxSizing: 'border-box',
                    border: `1.5px solid ${v.invalid ? colors.danger : v.filled ? colors.primary : colors.border}`,
                    borderRadius: 8, fontSize: 13, fontWeight: 800,
                    fontFamily: 'inherit', outline: 'none',
                    color: colors.text, background: v.invalid ? colors.dangerBg : '#fff',
                    textAlign: 'center',
                  }}
                />
                <span
                  style={{
                    fontSize: 10, color: colors.textMuted, fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  /{c.maxMarks}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Multi-component breakdown (view mode, saved) ──
          Same compact W / P / O row layout for consistency.
          Uses inline-flex so the pill actually clips overflow (span
          defaults to inline layout where overflow: hidden is ignored). */}
      {!isEditMode && hasSaved && !isSingle && (
        <div
          style={{
            paddingLeft: 52, display: 'flex', gap: 8, flexWrap: 'nowrap',
          }}
        >
          {subject.components.map((c) => {
            const val = savedComponents?.[c.type];
            return (
              <div
                key={c.type}
                style={{
                  flex: 1, minWidth: 0,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                  fontSize: 11, fontWeight: 700, padding: '4px 8px', borderRadius: 20,
                  background: '#fff', color: colors.text,
                  border: `1px solid ${colors.border}`,
                  overflow: 'hidden', whiteSpace: 'nowrap',
                }}
                title={c.label}
              >
                <span style={{ color: colors.textMuted, fontWeight: 800 }}>
                  {shortComponentLabel(c.type)}
                </span>
                <span>
                  {val}
                  <span style={{ opacity: 0.55, fontWeight: 600 }}>/{c.maxMarks}</span>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const ExamMarks: React.FC<Props> = ({ onBack, onNavigate }) => {
  const [view, setView] = useState<View>('exams');

  // ── View 1: exam list (fetched once on mount) ──────────────────────────
  const [examList,        setExamList]        = useState<OngoingExamListItem[]>([]);
  const [examListLoading, setExamListLoading] = useState(true);
  const [examListError,   setExamListError]   = useState('');

  // ── View 2: subjects for the picked exam (fetched on demand) ───────────
  const [selectedExam,     setSelectedExam]     = useState<OngoingExamListItem | null>(null);
  const [subjectsList,     setSubjectsList]     = useState<ExamSubjectListItem[]>([]);
  const [subjectsLoading,  setSubjectsLoading]  = useState(false);
  const [subjectsError,    setSubjectsError]    = useState('');

  // ── View 3: chosen subject + roster + marks state ──────────────────────
  const [selectedAssignment, setSelectedAssignment] = useState<SubjectAssignment | null>(null);
  const [subjectMeta,        setSubjectMeta]        = useState<ExamSubjectMeta | null>(null);
  const [students,           setStudents]           = useState<ExamStudent[]>([]);
  const [studentsLoading,    setStudentsLoading]    = useState(false);
  const [studentsError,      setStudentsError]      = useState('');
  const [search,             setSearch]             = useState('');

  // Class-wide edit mode: when true, every student row shows a marks input.
  const [isEditMode, setIsEditMode] = useState(false);
  /** studentId -> component type -> draft string. */
  const [drafts, setDrafts] = useState<
    Record<number, Partial<Record<ExamComponent, string>>>
  >({});
  // Keyed remount signal for SlideToSubmit so we can reset after a save/failure.
  const submitCounterRef = useRef(0);
  const [submitKey, setSubmitKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // ── Fetch the ongoing exam list on mount ────────────────────────────────
  const loadExamList = useCallback(async () => {
    setExamListLoading(true);
    setExamListError('');
    try {
      const list = await getOngoingExamList();
      setExamList(list);
    } catch (err) {
      setExamListError(err instanceof Error ? err.message : 'Failed to load exams.');
    } finally {
      setExamListLoading(false);
    }
  }, []);

  useEffect(() => { loadExamList(); }, [loadExamList]);

  // ── Fetch subjects list when an exam is picked ─────────────────────────
  const loadSubjectsForExam = useCallback(async (examId: number) => {
    setSubjectsLoading(true);
    setSubjectsError('');
    try {
      const list = await getExamSubjectsList(examId);
      setSubjectsList(list);
    } catch (err) {
      setSubjectsError(err instanceof Error ? err.message : 'Failed to load subjects.');
    } finally {
      setSubjectsLoading(false);
    }
  }, []);

  const filteredStudents = useMemo(() => {
    if (!search.trim()) return students;
    const q = search.toLowerCase();
    return students.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.rollNo.includes(q) ||
        s.uid.toLowerCase().includes(q),
    );
  }, [students, search]);

  const stats = useMemo(() => {
    if (!subjectMeta) return { total: students.length, filled: 0, pending: students.length };
    const filled = students.filter((s) => {
      const saved = s.marks[subjectMeta.subjectId];
      if (!saved) return false;
      // A student is "entered" only when every component has a saved value.
      return subjectMeta.components.every((c) => typeof saved[c.type] === 'number');
    }).length;
    return { total: students.length, filled, pending: students.length - filled };
  }, [students, subjectMeta]);

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleExamSelect = (exam: OngoingExamListItem) => {
    setSelectedExam(exam);
    setSelectedAssignment(null);
    setSubjectMeta(null);
    setSubjectsList([]);
    setSearch('');
    setView('assignments');
    loadSubjectsForExam(exam.id);
  };

  const loadStudentsForSubject = useCallback(
    async (examId: number, classId: number, sectionId: number, subjectId: number) => {
      setStudentsLoading(true);
      setStudentsError('');
      try {
        const resp = await getExamStudentList(examId, classId, sectionId, subjectId);
        // The students endpoint carries the authoritative component metadata
        // (has_theory / has_practical / has_oral + per-component max marks),
        // so we rebuild subjectMeta here rather than trusting the view-2 shape.
        setSubjectMeta(metaFromStudentListResponse(resp));
        setStudents(rosterFromStudentListResponse(resp));
      } catch (err) {
        setStudentsError(err instanceof Error ? err.message : 'Failed to load students.');
        setStudents([]);
      } finally {
        setStudentsLoading(false);
      }
    },
    [],
  );

  const handleAssignmentSelect = (item: ExamSubjectListItem) => {
    if (!selectedExam) return;
    const { assignment, meta } = toAssignmentAndMeta(item);
    setSelectedAssignment(assignment);
    setSubjectMeta(meta); // provisional; overwritten by the students API meta
    setStudents([]);
    setSearch('');
    setIsEditMode(false);
    setDrafts({});
    setView('students');
    loadStudentsForSubject(selectedExam.id, item.class_id, item.section_id, item.subject_id);
  };

  // ── Class-wide edit-mode handlers ──────────────────────────────────────
  /** Toggle the whole class into edit mode, seeding drafts from saved marks. */
  const handleEnterEditMode = () => {
    if (!subjectMeta) return;
    const seeded: Record<number, Partial<Record<ExamComponent, string>>> = {};
    for (const s of students) {
      const saved = s.marks[subjectMeta.subjectId];
      const perComponent: Partial<Record<ExamComponent, string>> = {};
      for (const c of subjectMeta.components) {
        const v = saved?.[c.type];
        perComponent[c.type] = typeof v === 'number' ? String(v) : '';
      }
      seeded[s.id] = perComponent;
    }
    setDrafts(seeded);
    setIsEditMode(true);
  };

  /** Discard any unsaved drafts and return to view mode. */
  const handleCancelEditMode = () => {
    setIsEditMode(false);
    setDrafts({});
    setSubmitError('');
  };

  const handleDraftChange = (studentId: number, component: ExamComponent, next: string) => {
    setDrafts((prev) => ({
      ...prev,
      [studentId]: { ...(prev[studentId] ?? {}), [component]: next },
    }));
  };

  /**
   * Build the ADD_MARKS payload from the current drafts, POST it, and
   * only commit the values locally on a 200 response. Partial or invalid
   * students are dropped from the payload; validation mirrors what the
   * SlideToSubmit label reports.
   *
   * On failure the drafts are preserved so the teacher can retry.
   */
  const handleSubmitAll = async () => {
    if (!subjectMeta || !selectedExam || submitting) return;
    const subjectId = subjectMeta.subjectId;

    // Collect ready (fully filled + valid) students into two shapes:
    // one for the API payload, one for the optimistic local update.
    const payloadEntries: AddMarksStudentEntry[] = [];
    const localUpdates: Record<number, Partial<Record<ExamComponent, number>>> = {};

    for (const s of students) {
      const per = drafts[s.id];
      if (!per) continue;
      const parsed: Partial<Record<ExamComponent, number>> = {};
      let ok = true;
      for (const c of subjectMeta.components) {
        const raw = (per[c.type] ?? '').trim();
        if (raw === '') { ok = false; break; }
        const n = Number(raw);
        if (isNaN(n) || n < 0 || n > c.maxMarks) { ok = false; break; }
        parsed[c.type] = n;
      }
      if (!ok) continue;
      localUpdates[s.id] = parsed;
      // The 'total' pseudo-component only appears when the subject has no
      // theory/practical/oral breakdown - it isn't a real key on the wire,
      // so we skip it in the payload (the API expects real component keys).
      const subjectMarks: { theory?: number; practical?: number; oral?: number } = {};
      if (parsed.theory    !== undefined) subjectMarks.theory    = parsed.theory;
      if (parsed.practical !== undefined) subjectMarks.practical = parsed.practical;
      if (parsed.oral      !== undefined) subjectMarks.oral      = parsed.oral;
      payloadEntries.push({
        student_id: s.id,
        subject_marks: { [String(subjectId)]: subjectMarks },
      });
    }

    if (payloadEntries.length === 0) return;

    setSubmitting(true);
    setSubmitError('');
    try {
      await addExamMarks(selectedExam.id, { marks: payloadEntries });
      // Commit locally so view mode shows the freshly-saved values without
      // needing to re-fetch the roster.
      setStudents((prev) =>
        prev.map((s) => {
          const parsed = localUpdates[s.id];
          if (!parsed) return s;
          return {
            ...s,
            marks: {
              ...s.marks,
              [subjectId]: { ...(s.marks[subjectId] ?? {}), ...parsed },
            },
          };
        }),
      );
      setIsEditMode(false);
      setDrafts({});
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to save marks. Try again.');
    } finally {
      setSubmitting(false);
      // Re-mount the slide component so its "confirmed" state resets whether
      // we succeeded or failed - on failure the teacher can slide again.
      submitCounterRef.current += 1;
      setSubmitKey(submitCounterRef.current);
    }
  };

  const goBackFromAssignments = () => {
    setSelectedExam(null);
    setSelectedAssignment(null);
    setSubjectMeta(null);
    setSubjectsList([]);
    setSubjectsError('');
    setView('exams');
  };

  const goBackFromStudents = () => {
    setSelectedAssignment(null);
    setSubjectMeta(null);
    setStudents([]);
    setStudentsError('');
    setSearch('');
    setIsEditMode(false);
    setDrafts({});
    setSubmitError('');
    setView('assignments');
  };

  const globalStyles = `
    @keyframes slideUp {
      from { opacity: 0; transform: translateY(14px); }
      to   { opacity: 1; transform: translateY(0);    }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes sheetSlideUp {
      from { transform: translateY(100%); }
      to   { transform: translateY(0);   }
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
  `;

  // ─── VIEW 1: Ongoing exams list ───────────────────────────────────────
  if (view === 'exams') {
    return (
      <PageShell title="Exam marks" onBack={onBack}>
        <style>{globalStyles}</style>

        <div style={{ padding: '16px 20px 8px', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span
              style={{
                display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                background: '#22c55e', boxShadow: '0 0 0 3px #dcfce7',
              }}
            />
            <span style={{ fontSize: 12, fontWeight: 800, color: colors.text, letterSpacing: '0.4px' }}>
              ONGOING EXAMS
            </span>
            {!examListLoading && !examListError && (
              <span
                style={{
                  fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                  background: '#dcfce7', color: '#166534',
                }}
              >
                {examList.length}
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, color: colors.textLight, marginTop: 6 }}>
            Pick an exam to enter marks for your subjects
          </div>
        </div>

        <div
          style={{
            flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
            padding: '10px 20px 110px',
            display: 'flex', flexDirection: 'column', gap: 10,
          }}
        >
          {examListLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 0', gap: 12 }}>
              <div style={{
                width: 30, height: 30,
                border: `3px solid ${colors.borderLight}`,
                borderTopColor: colors.primary, borderRadius: '50%',
                animation: 'spin 0.7s linear infinite',
              }} />
              <div style={{ fontSize: 13, color: colors.textMuted }}>Loading exams…</div>
            </div>
          ) : examListError ? (
            <div style={{
              background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`,
              borderRadius: 12, padding: '14px 16px',
              fontSize: 13, color: colors.danger, textAlign: 'center',
            }}>
              {examListError}
              <button
                onClick={loadExamList}
                style={{
                  display: 'block', margin: '10px auto 0',
                  padding: '6px 14px', borderRadius: 8,
                  border: `1px solid ${colors.danger}`, background: '#fff',
                  fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  color: colors.danger, fontFamily: 'inherit',
                }}
              >
                Retry
              </button>
            </div>
          ) : examList.length === 0 ? (
            <div
              style={{
                textAlign: 'center', padding: '48px 20px',
                background: '#fff', borderRadius: 14,
                border: `1px dashed ${colors.borderLight}`,
                color: colors.textMuted, fontSize: 13,
              }}
            >
              <div style={{ fontSize: 32, marginBottom: 8, opacity: 0.4 }}>-</div>
              No ongoing exams for your subjects right now.
            </div>
          ) : (
            examList.map((e, i) => (
              <ExamCard
                key={e.id}
                exam={e}
                index={i}
                onSelect={handleExamSelect}
              />
            ))
          )}
        </div>
        <BottomNav activeTab="exam-marks" onNavigate={onNavigate} />
      </PageShell>
    );
  }

  // ─── VIEW 2: Subjects/assignments under the picked exam ─────────────────
  if (view === 'assignments' && selectedExam) {
    return (
      <PageShell title="Exam marks" onBack={goBackFromAssignments}>
        <style>{globalStyles}</style>

        {/* Context banner */}
        <div style={{ padding: '14px 20px 0', flexShrink: 0 }}>
          <div
            style={{
              background: colors.primaryDark,
              borderRadius: 12, padding: '12px 16px',
              display: 'flex', flexDirection: 'column', gap: 2,
            }}
          >
            <div style={{ fontSize: 10, fontWeight: 700, color: '#EEE3F7', letterSpacing: '0.5px' }}>
              EXAM
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>
              {selectedExam.name}
              <span style={{ color: '#EEE3F7', fontWeight: 500 }}>
                {' · '}{EXAM_TYPE_LABEL[selectedExam.exam_type] ?? selectedExam.exam_type}{' · '}
                {fmtRange(selectedExam.start_date, selectedExam.end_date)}
              </span>
            </div>
          </div>
        </div>

        {/* Subjects label */}
        <div style={{ padding: '18px 20px 6px', flexShrink: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: colors.text, letterSpacing: '0.4px' }}>
            YOUR SUBJECTS FOR THIS EXAM
          </div>
          <div style={{ fontSize: 12, color: colors.textLight, marginTop: 4 }}>
            Pick a class + subject to open the student list
          </div>
        </div>

        {/* Assignments list */}
        <div
          style={{
            flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
            padding: '4px 20px 110px', display: 'flex', flexDirection: 'column', gap: 10,
          }}
        >
          {subjectsLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 0', gap: 12 }}>
              <div style={{
                width: 30, height: 30,
                border: `3px solid ${colors.borderLight}`,
                borderTopColor: colors.primary, borderRadius: '50%',
                animation: 'spin 0.7s linear infinite',
              }} />
              <div style={{ fontSize: 13, color: colors.textMuted }}>Loading subjects…</div>
            </div>
          ) : subjectsError ? (
            <div style={{
              background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`,
              borderRadius: 12, padding: '14px 16px',
              fontSize: 13, color: colors.danger, textAlign: 'center',
            }}>
              {subjectsError}
              <button
                onClick={() => loadSubjectsForExam(selectedExam.id)}
                style={{
                  display: 'block', margin: '10px auto 0',
                  padding: '6px 14px', borderRadius: 8,
                  border: `1px solid ${colors.danger}`, background: '#fff',
                  fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  color: colors.danger, fontFamily: 'inherit',
                }}
              >
                Retry
              </button>
            </div>
          ) : subjectsList.length === 0 ? (
            <div
              style={{
                textAlign: 'center', padding: '48px 20px',
                background: '#fff', borderRadius: 14,
                border: `1px dashed ${colors.borderLight}`,
                color: colors.textMuted, fontSize: 13,
              }}
            >
              No subjects assigned to you under this exam.
            </div>
          ) : (
            subjectsList.map((item, i) => {
              const { assignment, meta } = toAssignmentAndMeta(item);
              return (
                <AssignmentCard
                  key={assignment.assignmentId}
                  assignment={assignment}
                  examMeta={meta}
                  index={i}
                  onSelect={() => handleAssignmentSelect(item)}
                />
              );
            })
          )}
        </div>
        <BottomNav activeTab="exam-marks" onNavigate={onNavigate} />
      </PageShell>
    );
  }

  // ─── VIEW 3: Students list + marks entry ───────────────────────────────
  if (view === 'students' && selectedAssignment && selectedExam && subjectMeta) {
    return (
      <div style={{
        height: '100dvh', background: '#FDFCFE',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        fontFamily: "'Inter', sans-serif", position: 'relative',
      }}>
        <style>{globalStyles}</style>

        {/* Combined header - exam name is the title, and the class ·
            section · subject sits directly under it. Max/Min chips
            live on the right so no separate context banner is needed. */}
        <div style={{
          padding: '30px 20px 14px',
          display: 'flex', alignItems: 'flex-start', gap: 10,
          borderBottom: `1px solid ${colors.borderLight}`,
          background: '#fff', flexShrink: 0, zIndex: 10,
        }}>
          <button
            onClick={goBackFromStudents}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              display: 'flex', alignItems: 'flex-start', gap: 8,
              fontFamily: 'inherit', flex: 1, minWidth: 0, textAlign: 'left',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke={colors.text} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
              style={{ flexShrink: 0, marginTop: 2 }}>
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 16, fontWeight: 700, color: colors.text, lineHeight: 1.2,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {selectedExam.name}
              </div>
              <div style={{
                fontSize: 11, fontWeight: 600, color: colors.textMuted,
                marginTop: 3,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {selectedAssignment.className} - Sec {selectedAssignment.sectionName} · {subjectMeta.subjectName}
              </div>
            </div>
          </button>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
              background: colors.primaryBg, color: colors.primary,
              border: `1px solid ${colors.borderLight}`,
              textAlign: 'center',
            }}>
              Max {subjectMeta.maxMarks}
            </span>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
              background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0',
              textAlign: 'center',
            }}>
              Min {subjectMeta.minMarks}
            </span>
          </div>
        </div>

        {/* Stats */}
        <div style={{ padding: '12px 20px 0', display: 'flex', gap: 8, flexShrink: 0 }}>
          <div
            style={{
              flex: 1, background: colors.primaryBg,
              border: `1px solid ${colors.borderLight}`,
              borderRadius: 12, padding: '10px 8px', textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 900, color: colors.primary }}>
              {stats.total}
            </div>
            <div style={{ fontSize: 9, fontWeight: 700, color: colors.textMuted, letterSpacing: '0.5px', marginTop: 2 }}>
              TOTAL
            </div>
          </div>
          <div
            style={{
              flex: 1, background: '#f0fdf4',
              border: '1px solid #86efac', borderRadius: 12,
              padding: '10px 8px', textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 900, color: '#166534' }}>
              {String(stats.filled).padStart(2, '0')}
            </div>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#166534', letterSpacing: '0.5px', marginTop: 2 }}>
              ENTERED
            </div>
          </div>
          <div
            style={{
              flex: 1, background: '#fffbeb',
              border: '1px solid #fcd34d', borderRadius: 12,
              padding: '10px 8px', textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 20, fontWeight: 900, color: '#92400e' }}>
              {String(stats.pending).padStart(2, '0')}
            </div>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#92400e', letterSpacing: '0.5px', marginTop: 2 }}>
              PENDING
            </div>
          </div>
        </div>

        {/* Search bar */}
        <div style={{ padding: '12px 20px 0', flexShrink: 0 }}>
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: '#F3F4F6', borderRadius: 12,
              padding: '10px 14px',
              border: `1.5px solid ${colors.borderLight}`,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke={colors.textMuted} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search student by name or roll…"
              style={{
                flex: 1, background: 'none', border: 'none', outline: 'none',
                fontSize: 14, color: colors.text, fontFamily: 'inherit',
              }}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: 0, color: colors.textMuted, fontSize: 16, lineHeight: 1,
                }}
              >
                ×
              </button>
            )}
          </div>
        </div>

        {/* Full-width Add / Cancel button - kept on its own row so it can't
            be squeezed off-screen on narrow phones (e.g. Galaxy S8, 360px). */}
        <div style={{ padding: '10px 20px 0', flexShrink: 0 }}>
          {isEditMode ? (
            <button
              onClick={handleCancelEditMode}
              style={{
                width: '100%', padding: '11px 16px', borderRadius: 12,
                border: `1.5px solid ${colors.border}`, background: '#fff',
                fontSize: 14, fontWeight: 700, cursor: 'pointer',
                color: colors.text, fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                stroke={colors.text} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
              Cancel editing
            </button>
          ) : (
            <button
              onClick={handleEnterEditMode}
              style={{
                width: '100%', padding: '11px 16px', borderRadius: 12,
                border: 'none', background: colors.primaryDark,
                fontSize: 14, fontWeight: 700, cursor: 'pointer',
                color: '#fff', fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              {stats.filled > 0 ? 'Edit marks for whole class' : 'Add marks for whole class'}
            </button>
          )}
        </div>

        {/* Students list - bottom padding only reserves room for the
            slide-to-submit bar; in view mode the list can hug the bottom. */}
        <div
          style={{
            flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
            padding: isEditMode ? '12px 20px 130px' : '12px 20px 30px',
            display: 'flex', flexDirection: 'column', gap: 8,
          }}
        >
          {studentsLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 0', gap: 12 }}>
              <div style={{
                width: 30, height: 30,
                border: `3px solid ${colors.borderLight}`,
                borderTopColor: colors.primary, borderRadius: '50%',
                animation: 'spin 0.7s linear infinite',
              }} />
              <div style={{ fontSize: 13, color: colors.textMuted }}>Loading students…</div>
            </div>
          ) : studentsError ? (
            <div style={{
              background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`,
              borderRadius: 12, padding: '14px 16px',
              fontSize: 13, color: colors.danger, textAlign: 'center',
            }}>
              {studentsError}
              <button
                onClick={() => selectedExam && selectedAssignment && loadStudentsForSubject(
                  selectedExam.id,
                  selectedAssignment.classId,
                  selectedAssignment.sectionId,
                  selectedAssignment.subjectId,
                )}
                style={{
                  display: 'block', margin: '10px auto 0',
                  padding: '6px 14px', borderRadius: 8,
                  border: `1px solid ${colors.danger}`, background: '#fff',
                  fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  color: colors.danger, fontFamily: 'inherit',
                }}
              >
                Retry
              </button>
            </div>
          ) : filteredStudents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: colors.textMuted, fontSize: 14 }}>
              {search ? 'No students match your search.' : 'No students in this class.'}
            </div>
          ) : (
            filteredStudents.map((s, i) => (
              <StudentMarksRow
                key={s.id}
                student={s}
                subject={subjectMeta}
                index={i}
                isEditMode={isEditMode}
                drafts={drafts[s.id] ?? {}}
                onDraftChange={handleDraftChange}
              />
            ))
          )}
        </div>

        {/* Floating slide-to-submit - visible only while editing the class */}
        {isEditMode && (() => {
          // Count students with ALL components filled + valid vs. any
          // invalid component, so the CTA can reflect roster-wide state.
          let readyCount = 0;
          let invalidStudents = 0;
          for (const s of students) {
            const per = drafts[s.id];
            if (!per) continue;
            let allFilled = true;
            let anyInvalid = false;
            for (const c of subjectMeta.components) {
              const raw = (per[c.type] ?? '').trim();
              if (raw === '') { allFilled = false; continue; }
              const n = Number(raw);
              if (isNaN(n) || n < 0 || n > c.maxMarks) anyInvalid = true;
            }
            if (anyInvalid) invalidStudents += 1;
            else if (allFilled) readyCount += 1;
          }

          const disabled = submitting || readyCount === 0 || invalidStudents > 0;
          const label = submitting
            ? 'Saving marks…'
            : invalidStudents > 0
              ? `${invalidStudents} student${invalidStudents !== 1 ? 's' : ''} with invalid marks`
              : readyCount === 0
                ? 'Complete marks for at least one student'
                : `Slide to submit ${readyCount} student${readyCount !== 1 ? 's' : ''}`;

          return (
            <div
              style={{
                position: 'absolute', left: 0, right: 0, bottom: 0,
                zIndex: 20, pointerEvents: 'none',
              }}
            >
              <div
                style={{
                  height: 48,
                  background: 'linear-gradient(to bottom, rgba(253,252,254,0), rgba(253,252,254,1))',
                }}
              />
              <div style={{ background: '#FDFCFE', padding: '0 20px 32px', pointerEvents: 'all' }}>
                {submitError && (
                  <div
                    style={{
                      background: colors.dangerBg,
                      border: `1px solid ${colors.dangerBorder}`,
                      borderRadius: 10, padding: '8px 14px',
                      marginBottom: 10, fontSize: 13, color: colors.danger,
                      textAlign: 'center',
                    }}
                  >
                    {submitError}
                  </div>
                )}
                <SlideToSubmit
                  key={submitKey}
                  onConfirm={handleSubmitAll}
                  disabled={disabled}
                  label={label}
                  confirmedLabel="Marks saved!"
                />
              </div>
            </div>
          );
        })()}
      </div>
    );
  }

  // Fallback (shouldn't hit)
  return (
    <PageShell title="Exam marks" onBack={onBack}>
      <div style={{ padding: 40, textAlign: 'center', color: colors.textMuted }}>
        Something went wrong. Please go back.
      </div>
    </PageShell>
  );
};

export default ExamMarks;
